/**
 * costHooks — drafts/53 §B Cost of Quality.
 *
 * `useCostEntries({ sourceKind, sourceId })`    — list entries for one CAPA / deviation / finding
 * `useCostAggregates()`                          — trailing 12-month rollup for the dashboard tile
 * `useCreateCostEntry()`                         — POST + invalidate
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from './client';

export type CostSourceKind = 'CAPA' | 'DEVIATION' | 'AUDIT_FINDING' | 'OTHER';
export type CostCategory =
  | 'INTERNAL_LABOUR'
  | 'EXTERNAL_LAB'
  | 'MATERIAL_SCRAP'
  | 'EXPEDITED_SHIPPING'
  | 'REGULATORY_FEE'
  | 'OTHER';

export interface CostEntry {
  costEntryId: string;
  sourceKind: CostSourceKind;
  sourceId: string;
  costCategory: CostCategory;
  amountCents: number;
  currencyCode: string;
  incurredAtUtc: string;
  note: string | null;
  createdAtUtc: string;
  createdByUserId: string;
}

export interface CostAggregates {
  trailing12mTotalCents: number;
  currencyCode: string;
  byCategory: {
    sourceKind: CostSourceKind;
    costCategory: CostCategory;
    amountCentsTotal: number;
    currencyCode: string;
  }[];
  quarterSeries: {
    quarterStart: string;
    sourceKind: CostSourceKind;
    amountCentsTotal: number;
  }[];
}

export interface CreateCostEntryReq {
  sourceKind: CostSourceKind;
  sourceId: string;
  costCategory: CostCategory;
  amountCents: number;
  currencyCode: string;
  incurredAtUtc: string;
  note?: string;
}

export function useCostEntries(params: { sourceKind?: CostSourceKind; sourceId?: string }
): UseQueryResult<{ items: CostEntry[] }, Error> {
  const { sourceKind, sourceId } = params;
  return useQuery({
    queryKey: ['cost-entries', sourceKind, sourceId],
    queryFn: ({ signal }) => {
      const q = new URLSearchParams();
      if (sourceKind) q.set('source_kind', sourceKind);
      if (sourceId)   q.set('source_id', sourceId);
      const qs = q.toString();
      return apiFetch<{ items: CostEntry[] }>(
        '/api/v1/cost-entries' + (qs ? `?${qs}` : ''),
        { signal },
      );
    },
    enabled: !sourceKind || !!sourceId,    // when scoped to a source, require both
  });
}

export function useCostAggregates(): UseQueryResult<CostAggregates, Error> {
  return useQuery({
    queryKey: ['cost-entries', 'aggregates'],
    queryFn: ({ signal }) => apiFetch<CostAggregates>('/api/v1/cost-entries/aggregates', { signal }),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

export function useCreateCostEntry(): UseMutationResult<CostEntry, Error, CreateCostEntryReq> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch<CostEntry>('/api/v1/cost-entries', {
      method: 'POST',
      body,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cost-entries'] });
    },
  });
}
